<template>
  <div class="organization-settings-permissions role-permissions text-center">
    <div class="permissions-header row mb-10">
      <div class="col-sm-3 col-xs-2"></div>
      <div class="col-xs-2">
        <h6>
          Org
          <span class="no-wrap no-wrap-md">
            Manager
            <i
              v-tooltip
              class="hi hi-info-filled valign-middle"
              :data-original-title="tooltips.orgManager"
            />
          </span>
        </h6>
      </div>
      <div class="col-xs-2">
        <h6>
          Project
          <span class="no-wrap-md">
            Manager
            <i
              v-tooltip
              class="hi hi-info-filled valign-middle"
              :data-original-title="tooltips.projectManager"
            />
          </span>
        </h6>
      </div>
      <div class="col-xs-2">
        <h6>
          Project
          <span class="no-wrap-md">
            Viewer
            <i
              v-tooltip
              class="hi hi-info-filled valign-middle"
              :data-original-title="tooltips.projectViewer"
            />
          </span>
        </h6>
      </div>
      <div class="col-md-3 col-xs-3">
        <button
          type="button"
          class="btn btn-default custom-role-button"
          @click="toggleCustomDialog"
        >
          Custom role
        </button>
      </div>
    </div>

    <div class="permissions-body text-light">
      <div class="org-permissions mb-15">
        <h6 class="bg-gray-block-title text-uppercase border-bottom my-10 py-10 pl-10 text-left">Organization</h6>
        <div class="row px-10 py-10">
          <div class="col-sm-3 col-xs-2 text-left">
            {{ permissionLabels.edit_org_permissions }}
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.org_manager.edit_org_permissions"
                value="edit_org_permissions"
                name="permissions[]"
                data-role="org_manager"
                @change="updateSetting('org_manager', 'edit_org_permissions')"
              />
              <span class="custom-checkbox-box m-auto"></span>
            </label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-md-3 col-xs-3">
            <label class="my-0">—</label>
          </div>
        </div>
      </div>

      <div class="member-permissions mb-15">
        <h6 class="bg-gray-block-title text-uppercase border-bottom my-10 py-10 pl-10 text-left">Members</h6>
        <div class="row px-10 py-10">
          <div class="col-sm-3 col-xs-2 text-left">
            {{ permissionLabels.invite_members }}
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.org_manager.invite_members"
                value="invite_members"
                name="permissions[]"
                data-role="org_manager"
                @change="updateSetting('org_manager', 'invite_members')"
              />
              <span class="custom-checkbox-box m-auto"></span>
            </label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-md-3 col-xs-3">
            <label class="my-0">—</label>
          </div>
        </div>
      </div>

      <div class="activity-permissions mb-15">
        <div class="bg-gray-block-title text-uppercase border-bottom my-10 py-10 pl-10 text-left">
          <div class="d-flex align-items-center">
            <h6 class="m-0 mr-5">Activity</h6>
            <i
              v-tooltip
              class="hi hi-info-filled"
              :data-original-title="tooltips.activity"
            />
          </div>
        </div>
        <div class="row px-10 py-10">
          <div class="col-sm-3 col-xs-2 text-left">
            {{ permissionLabels['others_screenshots_descr'] }}
            <div v-if="exceptionsLabelFor('org_manager', 'others_screenshots')">
              <span
                v-tooltip="{ html: true }"
                :data-original-title="exceptionsTooltipFor('org_manager', 'others_screenshots')"
                class="text exception-label border-radius"
              >
                <i class="hi hi-info-filled" />
                {{ exceptionsLabelFor('org_manager', 'others_screenshots') }}
              </span>
            </div>
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.org_manager.others_screenshots"
                value="others_screenshots"
                name="permissions[]"
                data-role="org_manager"
                @click="openConfirmFor($event, 'org_manager', 'others_screenshots')"
              />
              <span class="custom-checkbox-box m-auto"></span>
            </label>
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.project_manager.others_screenshots"
                value="others_screenshots"
                name="permissions[]"
                disabled
                data-role="project_manager"
              />
              <span
                v-tooltip
                class="custom-checkbox-box m-auto"
                :data-original-title="tooltips.defaultPermission"
              ></span>
            </label>
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.project_viewer.others_screenshots"
                value="others_screenshots"
                name="permissions[]"
                data-role="project_viewer"
                @change="updateSetting('project_viewer', 'others_screenshots')"
              />
              <span class="custom-checkbox-box m-auto"></span>
            </label>
          </div>
          <div class="col-md-3 col-xs-3">
            <label class="my-0">—</label>
          </div>
        </div>
      </div>

      <div class="financial-permissions mb-15">
        <div class="bg-gray-block-title text-uppercase border-bottom my-10 py-10 pl-10 text-left">
          <div class="d-flex align-items-center">
            <h6 class="m-0 mr-5">Financials</h6>
            <i
              v-tooltip
              class="hi hi-info-filled"
              :data-original-title="tooltips.financials"
            />
          </div>
        </div>
        <div class="row px-10 py-10">
          <div class="col-sm-3 col-xs-2 text-left">
            {{ permissionLabels['manage_billing'] }}
            <div v-if="exceptionsLabelFor('org_manager', 'manage_billing')">
              <span
                v-tooltip="{ html: true }"
                :data-original-title="exceptionsTooltipFor('org_manager', 'manage_billing')"
                class="text exception-label border-radius"
              >
                <i class="hi hi-info-filled" />
                {{ exceptionsLabelFor('org_manager', 'manage_billing') }}
              </span>
            </div>
          </div>
          <div class="col-xs-2">
            <label class="custom-checkbox-wrapper blue-checkbox my-0">
              <input
                type="checkbox"
                :checked="permissionsData.org_manager.manage_billing"
                value="manage_billing"
                name="permissions[]"
                data-role="org_manager"
                @click="openConfirmFor($event, 'org_manager', 'manage_billing')"
              />
              <span class="custom-checkbox-box m-auto"></span>
            </label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-xs-2">
            <label class="my-0">—</label>
          </div>
          <div class="col-md-3 col-xs-3">
            <label class="my-0">—</label>
          </div>
        </div>
      </div>
    </div>
    <div class="permissions-footer d-flex panel panel-default border px-20 py-10">
      <span class="text-uppercase">Legend</span>
      <div class="items ml-auto">
        <label class="ml-30 custom-checkbox-wrapper blue-checkbox valign-middle default-cursor my-0 inline-block">
          <input
            type="checkbox"
            checked
            @click="$event.preventDefault()"
          />
          <span class="custom-checkbox-box"></span>
          <span class="valign-middle">ON</span>
        </label>
        <label class="ml-30 custom-checkbox-wrapper blue-checkbox valign-middle default-cursor my-0 inline-block">
          <input
            type="checkbox"
            checked
            disabled
            @click="$event.preventDefault()"
          />
          <span class="custom-checkbox-box"></span>
          <span class="valign-middle">Default setting</span>
        </label>
        <label class="ml-30 custom-checkbox-wrapper blue-checkbox valign-middle default-cursor my-0 inline-block">
          <input
            type="checkbox"
            @click="$event.preventDefault()"
          />
          <span class="custom-checkbox-box"></span>
          <span class="valign-middle">OFF</span>
        </label>
        <label class="ml-30 valign-middle my-0 inline-block"> — Not available </label>
      </div>
    </div>

    <AppDialog
      v-if="confirmationFor"
      show
      :title="confirmTitle"
      append-to-body
      class="permissions-confirm-dialog"
      @close="closeConfirm"
    >
      {{ confirmBody }}

      <template #footer>
        <div class="justify-content-end">
          <AppButton
            type="default"
            @click="closeConfirm"
          >
            Cancel
          </AppButton>
          <AppButton
            type="primary"
            @click="updateSetting(confirmationFor[0], confirmationFor[1])"
          >
            Confirm
          </AppButton>
        </div>
      </template>
    </AppDialog>
    <CustomRoleDialog
      v-if="visibleCustomRoleDialog"
      :organization-id="organizationId"
      :current-user-id="currentUserId"
      @close="toggleCustomDialog"
    />
  </div>
</template>

<script>
import CustomRoleDialog from './CustomRoleDialog.vue';
import RolePermissionMixin from '~/mixins/RolePermissionMixin';
import AppDialog from '~/components/AppDialog.vue';
import { pluralize } from '~/utils/string_utils';

export default {
  components: { CustomRoleDialog, AppDialog },
  mixins: [RolePermissionMixin],
  props: {
    currentPermissions: {
      // {org_manager: { edit_org_permissions: true, .. }, project_manager: [], project_viewer: []}
      type: Object,
      required: true,
    },
    exceptions: {
      type: Object,
      required: true, // { org_manager: {others_screenshots: [{ id:1, name: 'test' }, ...],..} }
    },
    organizationId: {
      type: Number,
      required: true,
    },
    currentUserId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      confirmationFor: null,
      permissionsData: this.currentPermissions,
      exceptionsData: this.exceptions,
      visibleCustomRoleDialog: false,
      permissionLabels: Object.freeze({
        edit_org_permissions: 'Change org level permissions  (view and edit permissions tab)',
        invite_members: 'Invite members or create member accounts',
        others_screenshots: 'View screenshots/activity',
        others_screenshots_descr: 'View screenshots/activity for other members',
        manage_billing: 'Manage financials',
      }),
      tooltips: Object.freeze({
        activity:
          'This feature has exceptions for the organization manager role. Manage permission exceptions at member profile.',
        financials:
          'This feature has exceptions for the organization manager role. Manage permission exceptions at member profile.',
        orgManager: 'Can do everything, except manage financials (depending on permissions) and send payments',
        projectManager:
          "Can manage, track, view screenshots of members they oversee and report for any project they're added to",
        projectViewer: 'Project viewers can view reports for any projects they’re added to.',
        defaultPermission: 'This permission is set by default for project managers',
      }),
    };
  },

  computed: {
    confirmTitle() {
      const [role, permissionKey] = this.confirmationFor;
      const state = this.permissionsData[role][permissionKey] ? 'OFF' : 'ON';

      return `Confirm changes - Turn ${state} ${this.permissionLabels[permissionKey]}?`;
    },
    confirmBody() {
      const permissionKey = this.confirmationFor[1];

      return `You're about to change ${this.permissionLabels[permissionKey]} permission for all Organization managers.
              Do you want to proceed with changing this permission?`;
    },
  },

  methods: {
    exceptionsTooltipFor(role, permissionKey) {
      const qtyExceptions = this.exceptionsData[role][permissionKey].length;
      const visibleNames = this.exceptionsData[role][permissionKey].slice(0, 5);
      const hiddenNames = this.exceptionsData[role][permissionKey].slice(5);
      const state = this.permissionsData[role][permissionKey] ? 'OFF' : 'ON';

      return `<div class="text-left">${pluralize(qtyExceptions, 'This org manager has', 'These org managers have')}
          "${this.permissionLabels[permissionKey]}"
          <b>${state}</b> in ${pluralize(qtyExceptions, 'his', 'their')} profile
          <ul class="pl-20 mb-0">${visibleNames.map((item) => `<li>${item.name}</li>`).join('')}</ul>
          ${hiddenNames.length ? `<div>+ ${hiddenNames.length} ${pluralize(hiddenNames.length, 'member')}</div>` : ''}
        </div>`;
    },
    qtyExceptionsFor(role, permissionKey) {
      return ((this.exceptionsData[role] || {})[permissionKey] || []).length;
    },
    exceptionsLabelFor(role, permissionKey) {
      const qty = this.qtyExceptionsFor(role, permissionKey);

      return qty === 0 ? '' : `${qty} ${pluralize(qty, 'exception')}`;
    },
    closeConfirm() {
      this.confirmationFor = null;
    },
    openConfirmFor(event, role, permissionKey) {
      event.preventDefault();
      this.confirmationFor = [role, permissionKey];
    },
    toggleCustomDialog() {
      this.visibleCustomRoleDialog = !this.visibleCustomRoleDialog;
    },
    updateSetting(role, permissionKey) {
      const flag = !this.permissionsData[role][permissionKey];

      this.saveSetting(role, permissionKey, flag)
        .then(() => {
          this.closeConfirm();
          this.$appMessage('Setting saved!');
          this.permissionsData[role][permissionKey] = flag;
          if (this.exceptionsData[role]) {
            this.exceptionsData[role][permissionKey] = [];
          }
        })
        .catch((msg) => {
          this.$appMessage(msg);
        });
    },
  },
};
</script>
